import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  InputLabel,
  FormControl,
  Alert,
  Checkbox,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import type { SelectChangeEvent } from '@mui/material';
import type { Rating } from '@segmently/schema-models/entities';
import type { RatingFormData, SortDirection } from '../types';
import { useRatings } from '../hooks/useRatings';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';

type SortField = 'rating' | 'ratingCount' | 'platform' | 'description';

const INITIAL_FORM_DATA: RatingFormData = {
  rating: '',
  ratingCount: '',
  platform: 'ios',
  description: '',
};

export default function RatingsTable({ projectId }: { projectId: string }) {
  const { ratings, error, setError, createRating, updateRating, deleteRating } = useRatings(projectId);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<Rating | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [formData, setFormData] = useState<RatingFormData>(INITIAL_FORM_DATA);

  const [sortField, setSortField] = useState<SortField>('rating');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const guideService = useMemo(() => new GuideService(), []);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === ratings.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(ratings.map(r => r.id).filter(Boolean)));
    }
  }, [selectedIds.size, ratings]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    try {
      await Promise.all(Array.from(selectedIds).map(id => deleteRating(id)));
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Error deleting selected ratings:', err);
      setError('Failed to delete selected ratings.');
    }
  }, [selectedIds, deleteRating, setError]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedRows = useMemo(() => {
    const s = [...ratings];
    s.sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      if (sortField === 'rating' || sortField === 'ratingCount') {
        av = Number(a[sortField]) || 0;
        bv = Number(b[sortField]) || 0;
      } else {
        av = (a[sortField] || '').toString().toLowerCase();
        bv = (b[sortField] || '').toString().toLowerCase();
      }
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return s;
  }, [ratings, sortField, sortDirection]);

  const openMenu = (e: React.MouseEvent<HTMLElement>, row: Rating) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };
  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleRowClick = (row: Rating) => {
    setSelectedRow(row);
    setDialogMode('edit');
    setFormData({
      rating: row.rating != null ? String(row.rating) : '',
      ratingCount: row.ratingCount != null ? String(row.ratingCount) : '',
      platform: (row.platform as 'ios' | 'android') ?? 'ios',
      description: row.description ?? '',
    });
    setOpenDialog(true);
  };

  const handleCreate = () => {
    setDialogMode('create');
    setFormData(INITIAL_FORM_DATA);
    setOpenDialog(true);
  };

  const handleEdit = () => {
    if (!selectedRow) return;
    setDialogMode('edit');
    setFormData({
      rating: selectedRow.rating != null ? String(selectedRow.rating) : '',
      ratingCount: selectedRow.ratingCount != null ? String(selectedRow.ratingCount) : '',
      platform: (selectedRow.platform as 'ios' | 'android') ?? 'ios',
      description: selectedRow.description ?? '',
    });
    setOpenDialog(true);
    closeMenu();
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      await deleteRating(selectedRow.id);
      closeMenu();
    } catch (err) {
      console.error('Delete rating failed:', err);
      setError('Failed to delete rating.');
    }
  };

  const handleSave = async () => {
    try {
      const payload: Partial<Rating> = {
        rating: String(parseFloat(formData.rating) || 0),
        ratingCount: String(parseInt(formData.ratingCount, 10) || 0),
        platform: formData.platform === 'android' ? 'android' : 'ios',
        description: formData.description || '',
      };

      if (dialogMode === 'create') {
        await createRating(payload);
      } else if (selectedRow) {
        await updateRating(selectedRow.id, payload);
      }

      setOpenDialog(false);
      setSelectedRow(null);
    } catch (err) {
      console.error('Save rating failed:', err);
      setError('Failed to save rating.');
    }
  };

  const handleCancel = () => {
    setOpenDialog(false);
  };

  const handleTextFieldChange = (field: keyof RatingFormData) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const handlePlatformChange = (e: SelectChangeEvent) => {
    setFormData({ ...formData, platform: e.target.value as 'ios' | 'android' });
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" gutterBottom>Ratings</Typography>
          {selectedIds.size > 0 && (
            <>
              <Chip label={selectedIds.size} size="small" color="primary" sx={{ fontWeight: 'medium' }} />
              <Tooltip title="Delete selected">
                <IconButton onClick={handleDeleteSelected} color="error" size="small" aria-label="Delete selected">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        <WithGuideHelp
          guideKey="ratings-create-rating-button"
          guideService={guideService}
          position="top-right"
        >
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            size="small"
            disabled={!projectId}
            data-testid="ratings-create-button"
          >
            Create Rating
          </Button>
        </WithGuideHelp>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <TableContainer>
        <Table sx={{ minWidth: 650, tableLayout: 'fixed', width: '100%' }} data-testid="ratings-table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={ratings.length > 0 && selectedIds.size === ratings.length}
                  indeterminate={selectedIds.size > 0 && selectedIds.size < ratings.length}
                  onChange={handleSelectAll}
                  disabled={ratings.length === 0}
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 120, whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'rating'}
                  direction={sortField === 'rating' ? sortDirection : 'asc'}
                  onClick={() => handleSort('rating')}
                >
                  Rating
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 140, whiteSpace: 'nowrap' }} align="right">
                <TableSortLabel
                  active={sortField === 'ratingCount'}
                  direction={sortField === 'ratingCount' ? sortDirection : 'asc'}
                  onClick={() => handleSort('ratingCount')}
                >
                  Rating Count
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 120, whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                <TableSortLabel
                  active={sortField === 'platform'}
                  direction={sortField === 'platform' ? sortDirection : 'asc'}
                  onClick={() => handleSort('platform')}
                >
                  Platform
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 200, whiteSpace: 'nowrap' }}>
                <TableSortLabel
                  active={sortField === 'description'}
                  direction={sortField === 'description' ? sortDirection : 'asc'}
                  onClick={() => handleSort('description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 80 }} align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length > 0 ? sortedRows.map((row) => (
              <TableRow
                key={row.id}
                hover
                onClick={() => handleRowClick(row)}
                sx={{ cursor: 'pointer' }}
                data-testid={`ratings-row-${row.id}`}
                selected={selectedIds.has(row.id)}
              >
                <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.has(row.id)}
                    onChange={() => handleToggleSelect(row.id)}
                    size="small"
                  />
                </TableCell>
                <TableCell sx={{ width: 120, whiteSpace: 'nowrap' }}>{row.rating ?? ''}</TableCell>
                <TableCell sx={{ width: 140, whiteSpace: 'nowrap' }} align="right">{row.ratingCount ?? ''}</TableCell>
                <TableCell sx={{ width: 120, textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{row.platform ?? ''}</TableCell>
                <TableCell sx={{ width: 200, whiteSpace: 'nowrap' }}>{row.description ?? ''}</TableCell>
                <TableCell sx={{ width: 80 }} align="right">
                  <IconButton size="small" onClick={(e) => openMenu(e, row)}>
                    <MoreVertIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            )) : (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No ratings found. Click &quot;Create Rating&quot; to add one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disablePortal
        slotProps={{ paper: { sx: { minWidth: 160 } } }}
        data-testid="ratings-menu"
      >
        <MenuItem onClick={handleEdit}><EditIcon sx={{ mr: 1 }} />Edit</MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}><DeleteIcon sx={{ mr: 1 }} />Delete</MenuItem>
      </Menu>

      <Dialog
        open={openDialog}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
              backdropFilter: 'blur(20px)',
            },
          },
        }}
        data-testid="ratings-dialog"
      >
        <DialogTitle
          sx={{
            display: 'grid',
            gridTemplateColumns: '40px 1fr 40px',
            alignItems: 'center',
            gap: 1,
            padding: '0 0 8px',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <IconButton
              onClick={handleCancel}
              size="small"
              aria-label="close dialog"
              sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {dialogMode === 'create' ? 'Create Rating' : 'Edit Rating'}
          </Box>
          <Box />
        </DialogTitle>
        <DialogContent
          sx={{
            padding: '0',
            my: 2,
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Rating"
              type="number"
              value={formData.rating}
              onChange={handleTextFieldChange('rating')}
              inputProps={{ step: '0.1', 'data-testid': 'ratings-field-rating' }}
              fullWidth
            />
            <TextField
              label="Rating Count"
              type="number"
              value={formData.ratingCount}
              onChange={handleTextFieldChange('ratingCount')}
              inputProps={{ 'data-testid': 'ratings-field-ratingCount' }}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="platform-label">Platform</InputLabel>
              <Select
                labelId="platform-label"
                label="Platform"
                value={formData.platform}
                onChange={handlePlatformChange}
                data-testid="ratings-field-platform"
              >
                <MenuItem value="ios">iOS</MenuItem>
                <MenuItem value="android">Android</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Description"
              value={formData.description}
              onChange={handleTextFieldChange('description')}
              multiline
              rows={3}
              fullWidth
              inputProps={{ 'data-testid': 'ratings-field-description' }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: '16px 0 0' }}>
          <Button
            onClick={handleSave}
            variant="contained"
            data-testid="ratings-save-button"
          >
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
